/**
 * Token-model selection for the PNET auth-platform migration.
 *
 * The backend verifiers (pnet-auth-php / -node / -python) are moving from
 * accepting the Cognito **ID token** (`token_use=id`, `aud=<client id>`) to the
 * Cognito **access token** (`token_use=access`, audience proven through the
 * `scope` claim under the resource identifier). This module decides, from env,
 * which token the UI sends as `Authorization: Bearer` and which OAuth scopes it
 * requests from the Hosted UI.
 *
 *   VITE_AUTH_TOKEN_KIND = id     (default) today's behaviour, unchanged
 *   VITE_AUTH_TOKEN_KIND = access send the access token everywhere
 *   VITE_AUTH_SCOPES              space-separated OAuth scopes (access mode, REQUIRED)
 *   VITE_AUTH_RESOURCE            RFC 8707 resource indicator / verifier audience
 *                                 (access mode, REQUIRED)
 *
 * The audience is PER ENVIRONMENT: it is the real API hostname of the
 * environment the build targets (`https://api-dev.partnership-net.com`,
 * `https://api-demo.partnership-net.com`, `https://api.partnership-net.com`),
 * so this module carries NO built-in audience or scope default. A build that
 * asks for `access` mode without both variables (or with scopes that do not
 * belong to the configured resource) is misconfigured; it logs a console
 * warning and falls back to `id` mode with the legacy scopes, so it can never
 * request another environment's scopes. See docs/auth-mode.md.
 *
 * Like `authMode.ts`, values are parsed once at module load.
 */

export type AuthTokenKind = 'id' | 'access';

/** Scopes the app has always requested in ID-token mode. Do not change. */
export const LEGACY_ID_TOKEN_SCOPES: readonly string[] = ['email', 'openid', 'profile'];

const allowedKinds = new Set<AuthTokenKind>(['id', 'access']);

export const parseAuthTokenKind = (raw: unknown): AuthTokenKind => {
  const value = String(raw ?? 'id')
    .trim()
    .toLowerCase();
  if (value === '') return 'id';
  return allowedKinds.has(value as AuthTokenKind) ? (value as AuthTokenKind) : 'id';
};

/** Split a space-separated scope list, de-duplicated; empty input yields `fallback` (default: none). */
export const parseAuthScopes = (raw: unknown, fallback: readonly string[] = []): string[] => {
  const parts = String(raw ?? '')
    .split(/\s+/)
    .map((scope) => scope.trim())
    .filter((scope) => scope.length > 0);
  const unique = Array.from(new Set(parts));
  return unique.length > 0 ? unique : [...fallback];
};

/** Absolute http(s) URL without a trailing slash; anything else yields `fallback` (default: empty). */
export const parseAuthResource = (raw: unknown, fallback = ''): string => {
  const value = String(raw ?? '')
    .trim()
    .replace(/\/+$/, '');
  return /^https?:\/\/[^\s/]+/i.test(value) ? value : fallback;
};

export interface ResolvedAuthTokenConfig {
  kind: AuthTokenKind;
  /** Scopes handed to Amplify's `loginWith.oauth.scopes`. */
  scopes: string[];
  /** RFC 8707 resource indicator / verifier audience; empty in `id` mode. */
  resource: string;
  /** Human-readable reasons for any fallback; empty when the input was consistent. */
  warnings: string[];
}

/**
 * Pure resolution of the token configuration from raw env values. Exported so
 * the fallback rules are unit-testable independently of `import.meta.env`.
 *
 * `access` mode requires a valid `resource` AND a non-empty scope list whose
 * resource-server scopes (`<url>/<scope>`) all sit under that resource.
 * Anything else falls back to `id` mode with the legacy scopes.
 */
export const resolveAuthTokenConfig = (raw: {
  kind?: unknown;
  scopes?: unknown;
  resource?: unknown;
}): ResolvedAuthTokenConfig => {
  const warnings: string[] = [];
  const legacy = (): ResolvedAuthTokenConfig => ({
    kind: 'id',
    scopes: [...LEGACY_ID_TOKEN_SCOPES],
    resource: '',
    warnings,
  });

  const kind = parseAuthTokenKind(raw.kind);
  const rawKindNormalised = String(raw.kind ?? '')
    .trim()
    .toLowerCase();
  if (rawKindNormalised !== '' && rawKindNormalised !== kind) {
    warnings.push(
      `[AUTH_TOKEN_KIND] Invalid VITE_AUTH_TOKEN_KIND="${rawKindNormalised}". Falling back to "id".`,
    );
  }
  if (kind !== 'access') return legacy();

  const resource = parseAuthResource(raw.resource);
  const scopes = parseAuthScopes(raw.scopes);

  if (resource === '') {
    warnings.push(
      String(raw.resource ?? '').trim() === ''
        ? '[AUTH_RESOURCE] VITE_AUTH_TOKEN_KIND=access but VITE_AUTH_RESOURCE is not set. ' +
            'The audience is per environment (e.g. https://api-dev.partnership-net.com) and has no default; ' +
            'falling back to "id" token mode with the legacy scopes.'
        : `[AUTH_RESOURCE] Invalid VITE_AUTH_RESOURCE="${String(raw.resource)}" (absolute http(s) URL required). ` +
            'Falling back to "id" token mode with the legacy scopes.',
    );
  }
  if (scopes.length === 0) {
    warnings.push(
      '[AUTH_SCOPES] VITE_AUTH_TOKEN_KIND=access but VITE_AUTH_SCOPES is not set. ' +
        'Falling back to "id" token mode with the legacy scopes.',
    );
  }
  if (resource !== '' && scopes.length > 0) {
    const foreign = scopes.filter((s) => s.includes('/') && !s.startsWith(`${resource}/`));
    if (foreign.length > 0) {
      warnings.push(
        `[AUTH_SCOPES] VITE_AUTH_SCOPES contains scopes outside VITE_AUTH_RESOURCE="${resource}": ` +
          `${foreign.join(' ')}. Falling back to "id" token mode with the legacy scopes.`,
      );
    }
  }

  if (warnings.length > 0) return legacy();
  return { kind: 'access', scopes, resource, warnings };
};

/** Minimal shape of `fetchAuthSession().tokens` we depend on (Amplify v6). */
export interface SessionTokensLike {
  idToken?: { toString(): string } | null;
  accessToken?: { toString(): string } | null;
}

/**
 * Return the raw JWT for the configured token kind from an Amplify session, or
 * `undefined` when that token is absent. Every place that previously read
 * `session.tokens?.idToken?.toString()` goes through here so the switch is
 * made in exactly one spot.
 */
export const selectSessionToken = (
  tokens: SessionTokensLike | null | undefined,
  kind: AuthTokenKind = authTokenKind,
): string | undefined => {
  const source = kind === 'access' ? tokens?.accessToken : tokens?.idToken;
  const value = source?.toString?.();
  return value && value.length > 0 ? value : undefined;
};

const resolved = resolveAuthTokenConfig({
  kind: import.meta.env.VITE_AUTH_TOKEN_KIND,
  scopes: import.meta.env.VITE_AUTH_SCOPES,
  resource: import.meta.env.VITE_AUTH_RESOURCE,
});

export const authTokenKind: AuthTokenKind = resolved.kind;
export const isAccessTokenMode = authTokenKind === 'access';

/**
 * Scopes handed to Amplify's `loginWith.oauth.scopes`. In `id` mode (including
 * the misconfiguration fallback) this is the historical list so the Hosted UI
 * consent/scopes are byte-for-byte unchanged.
 */
export const oauthScopes: string[] = resolved.scopes;

/**
 * RFC 8707 resource indicator (empty in `id` mode). NOTE: Amplify v6
 * `signInWithRedirect` does not forward arbitrary authorize parameters (only
 * login_hint/lang/nonce/prompt), so this value is not yet sent as `resource=`
 * on the Hosted UI request; the access token still carries `<resource>/<scope>`
 * scopes, which the verifiers accept as audience proof. Kept here so callers
 * have one source of truth.
 */
export const authResource: string = resolved.resource;

for (const warning of resolved.warnings) {
  console.warn(warning);
}
